/*
 * Copyright (c) Nexign, JSC, 2019-2025.
 */
/**
 * Sagas for Footer Modals
 *
 * @author Evgenij Tyan
 * @author Anton Panasenko
 * @author Yakov Gushchin
 * @author Maxim Fedorov
 * @author Aleksandr Perkhunov
 * @author Julia Ignatieva
 * @author Ilya Babanov
 * @author Nikolay Sviridov
 * @version 1.37.0
 * @since 1.0.0
 */

import { createElement } from 'react';

import find from 'lodash/find';
import { SagaIterator } from 'redux-saga';
import { call, put, select, take } from 'redux-saga/effects';

import { requestSaga } from '@/main/sagas/requestSaga';
import { EGTMWidgetPersonalAccountCloseType } from '@modules/analytics/GoogleTagManager/GTMOperations/GTMWidgetPersonalAccountOperations';
import {
    API_HOSTNAME,
    BASKET_ACCOUNT_ADV_PAY,
    CHECK_ACCESS_LEVEL,
    REPORT_ACCOUNT_ADV_PAY,
    SUBSCRIBER_MOBILE,
    ZONES_BASKET,
} from '@modules/app/Api';
import { appHistoryPush } from '@modules/app/appHistory';
import { appRequestDownloadFile } from '@modules/app/appRequest';
import { makeSelectTranslation } from '@modules/internationalization/intSelectors';
import { ITranslation } from '@modules/internationalization/types';
import { ELogBusinessEventName, ELogBusinessEventType, logBusinessEvent } from '@modules/logging';
import { modalOpen } from '@modules/modal/modalActions';
import { IModalRenderProps } from '@modules/modal/types/modalTypes';
import { AccessComponentKeyEnum, FeatureNameEnum } from '@modules/role/constants/guard/guardConstants';
import { makeSelectGuardComponentEnabled, makeSelectPageProductsEnabled } from '@modules/role/selectors/guardSelector';
import { tableBasketCountSelector, tableMetaButtonsSelector, tableMetaUrlSelector } from '@modules/table/selectors';
import { wizardStorageClearStepsAction } from '@modules/wizard/wizardActions';
import { EWizardName, WIZARD_STORAGE_CLEAR_STEPS_SUCCESS } from '@modules/wizard/wizardConstants';
import { makeSelectAuthFeatures } from '@pages/PageAuth/selectors/authSelectors';
import { IUserFeature } from '@pages/PageAuth/types/authTypes';
import {
    makeSelectorExpensesAnalyzerCurrentAccount,
    makeSelectorExpensesAnalyzerType,
} from '@pages/PageExpensesAnalyzer/pageExpensesAnalyzerSelectors';
import { EExpensesAnalyzerTypes } from '@pages/PageExpensesAnalyzer/types/pageExpensesAnalyzerTypes';
import { productsClearAction } from '@pages/PageProducts/tariffsAndServicesActions';
import { EProductsCategoryUrl } from '@pages/PageProducts/tariffsAndServicesConstants';
import { ExternalRoutePath, openExternalRoute, openRoute } from '@routes/constants/externalRoutes';
import { CLIENT_ID, RoutePath } from '@routes/constants/routesConstants';
import { stringFormat, stringFormatParams } from '@utils/stringFormat';

import { mobileFileCashLoading, mobileFileCashSuccessful } from '../actions/tableActions';
import { basketGetListStart, basketShowLoader } from '../components/footerModal/Basket/basketActions';
import BasketContainer, { IBasketContainerProps } from '../components/footerModal/Basket/components/BasketContainer';
import ChangeRoleModal, { STATUS_OK } from '../components/footerModal/ChangeRoleModal';
import CreateInquiry, { ICreateInquiryProps } from '../components/footerModal/CreateInquiry/components/CreateInquiry';
import FooterErrorModal, { IFooterErrorModalProps } from '../components/footerModal/FooterErrorModal';
import LabelEditorModal from '../components/footerModal/LabelEditor/components/LabelEditorModal';
import MobileCashErrorModal from '../components/footerModal/MobileCashErrorModal';
import MobileCashModal from '../components/footerModal/MobileCashModal';
import {
    TLoadMobileCashAction,
    TOpenBasketAction,
    TOpenCreateInquiryAction,
    TOpenLabelEditorAction,
    TOpenMobileCashAction,
    TOpenMobileChangeServiceAction,
    TOpenMobileChangeTariffAction,
    TTableFooterPayload,
} from '../types/tableTypes';

export function* openMobileChangeroleSaga(): SagaIterator {
    try {
        const URL = API_HOSTNAME + SUBSCRIBER_MOBILE + CHECK_ACCESS_LEVEL;
        const accessLevel = yield call(requestSaga, URL, 'GET');
        const status = accessLevel.status ?? '';
        const message = accessLevel.message ?? '';
        const isWizardChangeRoleEnabled =
            yield select(makeSelectGuardComponentEnabled(AccessComponentKeyEnum.PageWizardChangeRole));

        if (!message || !status) {
            wizardStorageClearStepsAction({ wizardName: EWizardName.CHANGE_ROLE });
            isWizardChangeRoleEnabled
                ? appHistoryPush(RoutePath.wizardChangeRoleFromSubscribers)
                : yield call(openExternalRoute, ExternalRoutePath.mobileRole);
        } else {
            yield put(
                modalOpen({
                    render: (props: IModalRenderProps) =>
                        createElement(ChangeRoleModal, {
                            onCloseModal: props.onCloseModal,
                            message: message,
                            status: status,
                        }),
                    onCloseCallback: () => {
                        if (status === STATUS_OK) {
                            wizardStorageClearStepsAction({ wizardName: EWizardName.CHANGE_ROLE });
                            isWizardChangeRoleEnabled
                                ? appHistoryPush(RoutePath.wizardChangeRoleFromSubscribers)
                                : openExternalRoute(ExternalRoutePath.mobileRole);
                        }
                    },
                }),
            );
        }
    } catch (err) {
        console.error(err);
    }
}

export function* openMobileCashSaga({ payload }: TOpenMobileCashAction): SagaIterator {
    yield put(
        modalOpen({
            onCrossClickCallback: () => {
                payload.onCloseCallback?.(EGTMWidgetPersonalAccountCloseType.cancelBtnIcon);
            },
            render: (props: IModalRenderProps) =>
                createElement(MobileCashModal, {
                    clientId: payload.clientId || '',
                    onApplyCallback: payload.onApplyCallback,
                    onCloseModal: props.onCloseModal,
                    onCancelClick: payload.onCloseCallback,
                    onErrorLoadCallback: payload.onErrorLoadCallback,
                    onSuccessLoadCallBack: payload.onSuccessLoadCallBack,
                }),
        }),
    );
}

export function* loadMobileCashSaga({ payload }: TLoadMobileCashAction): SagaIterator {
    try {
        let url;
        if (payload.clientId) {
            url = API_HOSTNAME + REPORT_ACCOUNT_ADV_PAY(payload.clientId, payload.amount);
        } else {
            url = API_HOSTNAME + BASKET_ACCOUNT_ADV_PAY(payload.amount);
        }
        yield put(mobileFileCashLoading(true));

        yield call(appRequestDownloadFile, url);

        yield put(mobileFileCashSuccessful(true));

        payload.onSuccessCallBack?.(payload.amount);
    } catch (e: any) {
        payload.onErrorCallback?.(e);
        yield put(mobileFileCashSuccessful(false));

        const response = yield call((error) => {
            return error.response;
        }, e);

        yield put(
            modalOpen({
                render: (props: IModalRenderProps) =>
                    createElement(MobileCashErrorModal, {
                        onCloseModal: props.onCloseModal,
                        code: response.error.code,
                    }),
            }),
        );
    } finally {
        yield put(mobileFileCashLoading(false));
    }
}

export function* openLabelEditorSaga({ payload }: TOpenLabelEditorAction): SagaIterator {
    yield put(
        modalOpen({
            render: (props: IModalRenderProps) =>
                createElement(LabelEditorModal, {
                    onCloseModal: props.onCloseModal,
                    type: payload,
                }),
        }),
    );
}

export function* openCreateInquirySaga(action: TOpenCreateInquiryAction): SagaIterator {
    yield put(
        modalOpen({
            render: (props: IModalRenderProps) =>
                createElement<ICreateInquiryProps>(CreateInquiry, {
                    type: action.payload.type,
                    onCloseModal: props.onCloseModal,
                    userId: action.payload.userId,
                }),
        }),
    );
}

/**
 * Check count of selected subscribers in table with max count for current product
 * @param isTariff - product can be service or tariff
 */
export function* checkOnMaxCountSubscribersSelected(isTariff: boolean): SagaIterator {
    const translation: ITranslation = yield select(makeSelectTranslation());

    const metaUrl = yield select(tableMetaUrlSelector);
    const buttons = yield select(tableMetaButtonsSelector(metaUrl));
    const checkedCount = yield select(tableBasketCountSelector);
    const maxCount = find(buttons, [
        'buttonName',
        isTariff
            ? AccessComponentKeyEnum.FooterButtonTariffChange
            : AccessComponentKeyEnum.FooterButtonRateServicesAndTariffOptions,
    ])?.maxCount;

    // check if count of checked subscribers more then max count
    if (maxCount && checkedCount > maxCount) {
        const message = isTariff
            ? stringFormatParams(translation.pageSubscriberMobile.changeTariff.maxCount, { count: maxCount })
            : stringFormatParams(translation.pageSubscriberMobile.changeService.maxCount, { count: maxCount });
        yield put(
            modalOpen({
                render: (props: IModalRenderProps) =>
                    createElement<IFooterErrorModalProps>(FooterErrorModal, {
                        message: message,
                        onCloseModal: props.onCloseModal,
                        title: translation.corporate.modal.attention,
                    }),
            }),
        );
        // number of subscribers are exceeded
        return false;
    }
    // is available
    return true;
}

/**
 * Check on feature flag, if product enable, need to check on subscribers zones
 */
export function* checkOnSubscribersFeatureProductEnabled(): SagaIterator {
    const features = yield select(makeSelectAuthFeatures());
    const featureProduct = features.find((feature: IUserFeature) => feature.name === FeatureNameEnum.PRODUCTS);

    return featureProduct?.enabled ?? false;
}

/**
 * Check amount of zones for selected subscribers
 */
export function* checkOnSubscribersMaxZonesSelected(isTariff: boolean): SagaIterator {
    const translation: ITranslation = yield select(makeSelectTranslation());

    try {
        const URL = API_HOSTNAME + SUBSCRIBER_MOBILE + ZONES_BASKET;
        const zones = yield call(requestSaga, URL, 'GET');
        const zonesCount = zones?.zones ?? [];

        // check if subscribers from different zones
        if (zonesCount && zonesCount.length > 1) {
            const message = isTariff
                ? translation.pageSubscriberMobile.changeTariff.maxZone
                : translation.pageSubscriberMobile.changeService.maxZone;
            yield put(
                modalOpen({
                    render: (props: IModalRenderProps) =>
                        createElement(FooterErrorModal, {
                            onCloseModal: props.onCloseModal,
                            message: message,
                            title: translation.corporate.modal.attention + '!',
                        }),
                }),
            );
            // number of subscribers zones are exceeded
            return false;
        }

        // check if subscribers haven't zones. Zone with id === 0 is undefined.
        if (zonesCount && zonesCount[0].id === 0) {
            const message = isTariff
                ? translation.pageSubscriberMobile.changeTariff.noZone
                : translation.pageSubscriberMobile.changeService.noZone;
            yield put(
                modalOpen({
                    render: (props: IModalRenderProps) =>
                        createElement(FooterErrorModal, {
                            onCloseModal: props.onCloseModal,
                            message: message,
                            title: translation.corporate.modal.attention + '!',
                        }),
                }),
            );
            return false;
        }

        // is available
        return true;
    } catch (e: any) {
        yield put(
            modalOpen({
                render: (props: IModalRenderProps) =>
                    createElement(FooterErrorModal, {
                        message: e,
                        onCloseModal: props.onCloseModal,
                    }),
            }),
        );
    }
}

export function* openProductPageSaga(isTariff: boolean, payload: TTableFooterPayload): SagaIterator {
    const isProductPageEnabled = yield select(makeSelectPageProductsEnabled());
    if (isProductPageEnabled) {
        yield put(productsClearAction());
        yield put(
            logBusinessEvent({
                eventName: ELogBusinessEventName.USER_PAGE_PRODUCT_BROWSE,
                type: ELogBusinessEventType.common,
            }),
        );
        const url = payload.clientId
            ? RoutePath.productsFromSubscriberId.replace(CLIENT_ID, payload.clientId)
            : RoutePath.productsFromSubscribers;

        isTariff
            ? appHistoryPush(`${url}#${EProductsCategoryUrl.TARIFFS}`)
            : appHistoryPush(`${url}#${EProductsCategoryUrl.SERVICES}`);
    } else {
        if (isTariff) {
            yield put(
                logBusinessEvent({
                    eventName: ELogBusinessEventName.USER_RATE_PLAN_MASTER_TO_STEP_1,
                    type: ELogBusinessEventType.common,
                }),
            );
            const url = payload.clientId
                ? stringFormat(ExternalRoutePath.mobilePlanId, payload.clientId)
                : ExternalRoutePath.mobilePlan;
            openExternalRoute(url);
        } else {
            yield put(
                logBusinessEvent({
                    eventName: ELogBusinessEventName.USER_SERVICES_MASTER_TO_STEP_1,
                    type: ELogBusinessEventType.common,
                }),
            );
            const url = payload.clientId
                ? stringFormat(ExternalRoutePath.mobileServiceId, payload.clientId)
                : ExternalRoutePath.mobileService;
            openExternalRoute(url);
        }
    }
}

export function* openMobileChangeTariffSaga({ payload }: TOpenMobileChangeTariffAction): SagaIterator {
    if (!payload.clientId) {
        const isAvailableByMaxCountSelected: boolean = yield call(checkOnMaxCountSubscribersSelected, true);
        if (!isAvailableByMaxCountSelected) return;
    }

    const isProductFeatureEnabled = yield call(checkOnSubscribersFeatureProductEnabled);
    const isProductPageEnabled = yield select(makeSelectPageProductsEnabled());
    if ((!isProductFeatureEnabled || isProductPageEnabled) && !payload.clientId) {
        const isAvailableByMaxZoneSelected = yield call(checkOnSubscribersMaxZonesSelected, true);
        if (!isAvailableByMaxZoneSelected) return;
    }

    yield call(openProductPageSaga, true, payload);
}

export function* openMobileChangeServiceSaga({ payload }: TOpenMobileChangeServiceAction): SagaIterator {
    if (!payload.clientId) {
        const isAvailableByMaxCountSelected: boolean = yield call(checkOnMaxCountSubscribersSelected, false);
        if (!isAvailableByMaxCountSelected) return;
    }

    const isProductFeatureEnabled = yield call(checkOnSubscribersFeatureProductEnabled);
    const isProductPageEnabled = yield select(makeSelectPageProductsEnabled());
    if ((!isProductFeatureEnabled || isProductPageEnabled) && !payload.clientId) {
        const isAvailableByMaxZoneSelected = yield call(checkOnSubscribersMaxZonesSelected, false);
        if (!isAvailableByMaxZoneSelected) return;
    }

    yield call(openProductPageSaga, false, payload);
}

export function* openFooterWizardExpensesDetailSaga(): SagaIterator {
    // Clear previous master
    yield put(
        wizardStorageClearStepsAction({
            wizardName: EWizardName.EXPENSES_DETAIL,
        }),
    );
    yield take(WIZARD_STORAGE_CLEAR_STEPS_SUCCESS);

    const expensesAnalyzerType: EExpensesAnalyzerTypes = yield select(makeSelectorExpensesAnalyzerType());
    const currentAccount: number = yield select(makeSelectorExpensesAnalyzerCurrentAccount());
    let url = RoutePath.wizardExpensesDetail;
    if (expensesAnalyzerType === EExpensesAnalyzerTypes.AccountSingle && currentAccount) {
        url = RoutePath.wizardExpensesDetailAccountId.replace(CLIENT_ID, currentAccount.toString());
    }
    // start master
    openRoute(url);
}

export function* openBasketSaga(action: TOpenBasketAction): SagaIterator {
    const { gtmProps, title } = action.payload;
    yield put(basketShowLoader());
    yield put(basketGetListStart({ start: true }));
    yield put(
        modalOpen({
            modalParams: {
                isCustomCrossButton: true,
            },
            render: (props: IModalRenderProps) =>
                createElement<IBasketContainerProps>(BasketContainer, {
                    onClose: props.onCloseModal,
                    gtmProps,
                    title,
                }),
        }),
    );
}

